"use client";

import { cn } from "@/lib/utils";
import { type ReactNode, useEffect, useRef, useState } from "react";

type SiteResponsiveScaleProps = {
  children: ReactNode;
  className?: string;
  minWidth?: number;
};

const DEFAULT_MIN_WIDTH = 1024;

export function SiteResponsiveScale({
  children,
  className,
  minWidth = DEFAULT_MIN_WIDTH,
}: SiteResponsiveScaleProps) {
  const outerRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [scaleState, setScaleState] = useState({
    scale: 1,
    height: undefined as number | undefined,
  });

  useEffect(() => {
    let frameId = 0;

    const updateScale = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => {
        const outer = outerRef.current;
        const inner = innerRef.current;

        if (!outer || !inner) {
          return;
        }

        const viewportWidth = outer.clientWidth || window.innerWidth;
        const nextScale = viewportWidth < minWidth ? viewportWidth / minWidth : 1;
        const nextHeight = nextScale < 1 ? inner.scrollHeight * nextScale : undefined;

        setScaleState((previous) => {
          if (previous.scale === nextScale && previous.height === nextHeight) {
            return previous;
          }

          return {
            scale: nextScale,
            height: nextHeight,
          };
        });
      });
    };

    updateScale();

    const resizeObserver = new ResizeObserver(updateScale);
    if (innerRef.current) {
      resizeObserver.observe(innerRef.current);
    }

    window.addEventListener("resize", updateScale);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateScale);
    };
  }, [minWidth]);

  const isScaled = scaleState.scale < 1;

  return (
    <div
      ref={outerRef}
      className={cn("w-full", className)}
      style={isScaled
        ? { height: scaleState.height, overflowX: "clip" }
        : { overflowX: "clip" }}
    >
      <div
        ref={innerRef}
        style={isScaled
          ? {
            width: minWidth,
            transform: `scale(${scaleState.scale})`,
            transformOrigin: "top left",
          }
          : undefined}
      >
        {children}
      </div>
    </div>
  );
}
